import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import Sidebar from '../../components/ui/Sidebar';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import TemplateCard from './components/TemplateCard';
import FilterPanel from './components/FilterPanel';
import TemplatePreviewModal from './components/TemplatePreviewModal';
import FeaturedTemplates from './components/FeaturedTemplates';
import SearchBar from './components/SearchBar';

const TemplateMarketplace = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [hasActiveSubscription, setHasActiveSubscription] = useState(false);
  const [isCheckingSubscription, setIsCheckingSubscription] = useState(true);
  const [filters, setFilters] = useState({
    category: 'all',
    industry: 'all',
    features: [],
    sortBy: 'popular'
  });

  // Check user subscription status
  useEffect(() => {
    const checkSubscription = async () => {
      if (!user) {
        setIsCheckingSubscription(false);
        return;
      }

      try {
        const { data, error } = await supabase?.from('subscriptions')?.select('status')?.eq('user_id', user?.id)?.single();

        if (error) throw error;
        
        setHasActiveSubscription(data?.status === 'active');
      } catch (error) {
        console.error('Error checking subscription:', error);
        setHasActiveSubscription(false);
      } finally {
        setIsCheckingSubscription(false);
      }
    };

    checkSubscription();
  }, [user]);

  const templates = [];

  const featuredTemplates = templates?.filter((t) => t?.featured);

  const filteredTemplates = templates?.filter((template) => {
    const matchesSearch = !searchQuery ||
    template?.name?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    template?.description?.toLowerCase()?.includes(searchQuery?.toLowerCase()) ||
    template?.features?.some((f) => f?.toLowerCase()?.includes(searchQuery?.toLowerCase()));

    const matchesCategory = filters?.category === 'all' || template?.category === filters?.category;

    const matchesFeatures = filters?.features?.length === 0 ||
    filters?.features?.every((feature) =>
    template?.features?.some((f) => f?.toLowerCase()?.includes(feature?.toLowerCase()))
    );

    return matchesSearch && matchesCategory && matchesFeatures;
  });

  const sortedTemplates = [...filteredTemplates]?.sort((a, b) => {
    switch (filters?.sortBy) {
      case 'newest':
        return new Date(b.lastUpdated) - new Date(a.lastUpdated);
      case 'rating':
        return b?.rating - a?.rating;
      case 'downloads':
        return parseInt(b?.downloads?.replace('k', '000')) - parseInt(a?.downloads?.replace('k', '000'));
      default:
        return 0;
    }
  });

  const handleToggleFavorite = (templateId) => {
    const templateIndex = templates?.findIndex((t) => t?.id === templateId);
    if (templateIndex !== -1) {
      templates[templateIndex].isFavorite = !templates?.[templateIndex]?.isFavorite;
    }
  };

  const handleUseTemplate = (template) => {
    // Check subscription before allowing template use
    if (!user) {
      navigate('/login', { state: { returnUrl: '/template-marketplace' } });
      return;
    }

    if (!hasActiveSubscription) {
      navigate('/subscription-management', { 
        state: { 
          message: 'Subscribe to access all templates',
          returnUrl: '/template-marketplace'
        } 
      });
      return;
    }

    navigate('/visual-builder', { state: { template } });
  };

  const handleClearFilters = () => {
    setFilters({
      category: 'all',
      industry: 'all',
      features: [],
      sortBy: 'popular'
    });
    setSearchQuery('');
  };

  if (isCheckingSubscription) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="flex flex-col items-center gap-4">
          <Icon name="Loader2" size={48} className="text-primary animate-spin" />
          <p className="text-muted-foreground">Checking subscription status...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background flex">
      <Sidebar
        isCollapsed={isSidebarCollapsed}
        onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
        user={user}
        onLogout={() => {}} />

      <div
        className={`flex-1 flex flex-col transition-all duration-250 ${
        isSidebarCollapsed ? 'lg:ml-20' : 'lg:ml-60'}`
        }>

        <div className="sticky top-0 z-20 bg-card border-b border-border">
          <div className="px-4 md:px-6 lg:px-8 py-4">
            <div className="flex items-center justify-between gap-4">
              <div className="flex items-center gap-3 min-w-0">
                <div className="w-10 h-10 bg-primary text-primary-foreground rounded-lg flex items-center justify-center flex-shrink-0">
                  <Icon name="Layers" size={20} />
                </div>
                <div className="min-w-0">
                  <h1 className="font-heading font-semibold text-xl md:text-2xl text-foreground truncate">
                    Template Marketplace
                  </h1>
                  <p className="text-sm text-muted-foreground hidden sm:block">
                    {hasActiveSubscription 
                      ? `All ${templates?.length} templates included with your subscription`
                      : `Subscribe to access ${templates?.length} professional templates`
                    }
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                {!hasActiveSubscription && user && (
                  <Button
                    variant="accent"
                    iconName="Crown"
                    iconPosition="left"
                    onClick={() => navigate('/subscription-management')}
                    className="hidden md:flex">
                    Subscribe Now
                  </Button>
                )}
                <Button
                  variant="default"
                  iconName="Plus"
                  iconPosition="left"
                  onClick={() => navigate('/visual-builder')}
                  className="hidden md:flex">
                  Start from Scratch
                </Button>
              </div>
            </div>
          </div>
        </div>

        {!hasActiveSubscription && user && (
          <div className="mx-4 md:mx-6 lg:mx-8 mt-6 p-4 bg-accent/10 border border-accent/20 rounded-lg flex items-start gap-3">
            <Icon name="Info" size={20} className="text-accent flex-shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="text-sm text-foreground font-medium mb-1">
                Templates Included with Subscription
              </p>
              <p className="text-sm text-muted-foreground">
                Subscribe to our monthly plan to unlock access to all {templates?.length} professional templates. 
                No additional fees - all templates are included!
              </p>
            </div>
            <Button
              variant="accent"
              size="sm"
              onClick={() => navigate('/subscription-management')}>
              View Plans
            </Button>
          </div>
        )}

        <main className="flex-1 px-4 md:px-6 lg:px-8 py-6 md:py-8 space-y-6 md:space-y-8">
          <div className="max-w-3xl">
            <SearchBar
              value={searchQuery}
              onChange={setSearchQuery}
              onSearch={(query) => setSearchQuery(query)} />
          </div>

          <FeaturedTemplates
            templates={featuredTemplates}
            onPreview={setSelectedTemplate}
            onUseTemplate={handleUseTemplate} />

          <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
            <div className="lg:col-span-1">
              <FilterPanel
                filters={filters}
                onFilterChange={setFilters}
                onClearFilters={handleClearFilters}
                resultCount={sortedTemplates?.length} />
            </div>

            <div className="lg:col-span-3">
              {sortedTemplates?.length > 0 ? (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 md:gap-6">
                  {sortedTemplates?.map((template) => (
                <TemplateCard
                  key={template?.id}
                  template={template}
                  onPreview={setSelectedTemplate}
                  onUseTemplate={handleUseTemplate}
                  onToggleFavorite={handleToggleFavorite}
                  hasActiveSubscription={hasActiveSubscription} />
                ))}
                </div>
              ) : (
              <div className="flex flex-col items-center justify-center py-16 md:py-24">
                  <div className="w-16 h-16 md:w-20 md:h-20 bg-muted rounded-full flex items-center justify-center mb-4">
                    <Icon name="Search" size={32} className="text-muted-foreground" />
                  </div>
                  <h3 className="font-heading font-semibold text-lg md:text-xl text-foreground mb-2">
                    No templates found
                  </h3>
                  <p className="text-sm md:text-base text-muted-foreground text-center mb-6 max-w-md">
                    Try adjusting your filters or search query to find what you're looking for
                  </p>
                  <Button
                  variant="outline"
                  iconName="RotateCcw"
                  iconPosition="left"
                  onClick={handleClearFilters}>
                    Clear All Filters
                  </Button>
                </div>
              )}
            </div>
          </div>
        </main>

        <footer className="border-t border-border bg-card px-4 md:px-6 lg:px-8 py-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <p className="text-sm text-muted-foreground text-center md:text-left">
              © {new Date()?.getFullYear()} DKS Creator. All rights reserved.
            </p>
            <div className="flex items-center gap-6">
              <a href="#" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                Help Center
              </a>
              <a href="#" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                Documentation
              </a>
              <a href="#" className="text-sm text-muted-foreground hover:text-foreground transition-colors">
                Contact Support
              </a>
            </div>
          </div>
        </footer>
      </div>
      {selectedTemplate && (
      <TemplatePreviewModal
        template={selectedTemplate}
        onClose={() => setSelectedTemplate(null)}
        onUseTemplate={handleUseTemplate}
        hasActiveSubscription={hasActiveSubscription} />
      )}
    </div>
  );
};

export default TemplateMarketplace;